import {
   Component, ViewChild, ElementRef, Input,
   OnInit, OnDestroy, OnChanges,
   Output, EventEmitter
} from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material';
import * as _ from 'lodash';

// vis
import * as vis from 'vis';

// config
import { navigationConfig } from '../../config/navigation.config';

// Core
import { ErrorMessage } from '../../../core/model/error-message';
import { IBoolResult, BoolResult } from '../../../core/interfaces/bool-result';
import { UiMessageSnackService } from '../../../core/components/message-snack/service/message-snack.service';

// Redux
import { NgRedux, select } from '@angular-redux/store';
import { IAppState } from '../../../core/store/store';

// foreign feature
import { IBreadcrumbItem } from '../../../core/components/breadcrumb/model/breadcrumb.interface';

// Own feature
import { NavigationService } from '../../service/navigation.service';
import { NavElementsContainer } from '../../model/nav-elements-container';
import { INavDisplayOptions, NAV_DISPLAY_OPTIONS_INITIAL_STATE } from '../../model/nav-display-options.interface';
import { INavGraphDisplayOptionEdges } from '../../model/nav-display-edgegroup-pairs';
import {
   navigationStoreResult2stInitialValue,
   navigationStore2stRequestOptions,
   navigationStore2stDisplayOptionEdges,
   navigationStoreResult2stDetail,
} from '../../store/actions';
import { NavElementsRoot } from '../../model/nav-elements-root';
import { INavGraph2EdgeDefinitionConfig } from '../../model/nav-graph2-edgedef-config.interface';
import { NavVisOptions } from '../../model/nav-vis-options';
import { INavGraph2SelResult, NAV_GRAPH_SEL_RESULT_INIT } from '../../model/nav-graph2-sel-result.interface';
import { LoadingphaseService } from '../../service/loadingphase.service';

@Component({
   selector: 'app-graph2',
   templateUrl: './nav-graph2.html',
   styleUrls: ['./nav-graph2.scss']
})
export class NavGraph2Component implements OnInit, OnDestroy, OnChanges {

   private _selectedNodes: any;
   // loading data
   public set _loadingPhase(value: boolean) {
      this._loadingphaseService.setContentBlockState(value);
      this._loadingphaseService.setLoadingphase(value, 'nav_graph');
   }

   // graph container
   private navElementContainer: NavElementsContainer = null;
   private _graph: any = null;

   // selection
   private _rightClickSelectedNodeID = '';

   // drawer
   public drawerIsOpen = false;

   // edge definition config
   private _edgeDefinitionConfig: INavGraph2EdgeDefinitionConfig = null;
   private _arrCompTypeToSelect: string[] = null;

   // result structure
   private _result: INavGraph2SelResult = NAV_GRAPH_SEL_RESULT_INIT;    // initialized mit ''

   // vis options
   private _visOptions = {
      autoResize: true,
      clickToUse: false,
      width: '100%',
      height: '97%',
      edges: {
         arrows: 'to',
         width: 1,
         font: {
            size: 9,
         }
      },
      locale: 'en',
      nodes: {
         shape: 'box',
         color: {
            highlight: {
               border: '#FF0000',
            }
         }
      },
      interaction: {
         hover: true,
         navigationButtons: false,
         selectable: true,
         tooltipDelay: 300
      }
   };

   // Input
   @Input() public elementsResultInit: any = null;
   @Input() public elementOptions: INavDisplayOptions = NAV_DISPLAY_OPTIONS_INITIAL_STATE;
   @Input() public breadcrumbSelected: IBreadcrumbItem = null;

   // Children
   @ViewChild('drawingArea') private _containerElemRef: ElementRef;
   // Context Menu
   @ViewChild('contextMenuContainer')
   public readonly _contextMenuContainerElemRef: ElementRef;
   @ViewChild(MatMenuTrigger)
   public readonly _contextMenuTrigger: MatMenuTrigger;

   // observables
   @select((s: IAppState) => s.navigationState.element2stDisplayOptionEdges) displayOptionEdges$;

   // events
   @Output() canSelectKomponent = new EventEmitter<INavGraph2SelResult | null>();

   constructor(
      private _store: NgRedux<IAppState>,
      private _navigationService: NavigationService,
      private _messageSnackService: UiMessageSnackService,
      private _loadingphaseService: LoadingphaseService,
   ) {
      this.navElementContainer = new NavElementsContainer(this._messageSnackService);
   }

   ngOnInit() {
      this._store.dispatch(navigationStoreResult2stDetail(null));

      // initialize navElementContainer if not already done
      if (!this.navElementContainer.getVirtualRoot()) {
         this.navElementContainer.computeInitialSearchResult(
            this._store.getState().navigationState.elementsResult2stInitial,
            this._store.getState().navigationState.config
         );
         this.navElementContainer.applyDisplayFilter(this.elementOptions);

         // set breadcrumb
         const ner: NavElementsRoot = this.navElementContainer.getVirtualRoot();

      }
      NavVisOptions.setVisOptions(this._visOptions, this._store.getState().navigationState.config);

      // set edge group pairs for selection
      const displayOptionEdges: INavGraphDisplayOptionEdges[] =
         _.cloneDeep(this._store.getState().navigationState.element2stDisplayOptionEdges);
      this.navElementContainer.fillDisplayOptionEdges(displayOptionEdges);
      this._store.dispatch(navigationStore2stDisplayOptionEdges(displayOptionEdges));

      // setup graph
      this._graph = new vis.Network(
         this._containerElemRef.nativeElement,
         this.navElementContainer.generateVisData(this._store.getState().navigationState, this.elementOptions),
         this._visOptions);

      // user interaction
      this._graph.on('selectNode', this.onSelectNode.bind(this));
      this._graph.on('deselectNode', this.onDeselectNode.bind(this));
      this._graph.on('oncontext', this.onContext.bind(this));
      this._graph.once('dragging', this.onDragging.bind(this));
      this._graph.on('dragEnd', this.onDragEnd.bind(this));
      this._graph.on('hoverNode', this.onHoverNode.bind(this));
      this._graph.on('blurEdge', this.onBlurEdge.bind(this));
      this._graph.on('zoom', this.onZoom.bind(this, this._graph, 0.7));
      this._graph.on('stabilizationProgress', this.onStabilizationProgress.bind(this));
      this._graph.on('stabilizationIterationsDone', this.onStabilizationIterationsDone.bind(this));

      // edge definition config
      // this._edgeDefinitionConfig = this._store.getState().navigationState.element2stEdgeDefinitionConfig;
   }

   ngOnDestroy() {
      if (this._graph) {
         this._graph.destroy();
         this._graph = null;
      }
      this.navElementContainer = null;
   }

   ngOnChanges(changes: any) {
      if (!this.elementsResultInit || !this._graph) { return; }

      // breadcrumb
      if (changes.breadcrumbSelected) {
         const ch = changes.breadcrumbSelected;
         if (ch.firstChange || ch.currentValue !== ch.previousValue) {
            this.onSelectNode({
               event: {
                  srcEvent: {
                     ctrlKey: false
                  }
               },
               nodes: [
                  ch.currentValue.value
               ]
            }, true);
            return;
         }
      }

      // parse searchResult
      if (changes.elementsResultInit) {            // detect changes
         const ch = changes.elementsResultInit;
         if (ch.firstChange || ch.currentValue !== ch.previousValue) {
            // setup container
            this.navElementContainer.computeInitialSearchResult(
               this.elementsResultInit,
               this._store.getState().navigationState.config
            );
         }
      }
      this.navElementContainer.applyDisplayFilter(this.elementOptions);

      // setup graph
      this._graph.setData(this.navElementContainer.generateVisData(this._store.getState().navigationState, this.elementOptions));
      this._graph.redraw();

      // set breadcrumb
      const ner: NavElementsRoot = this.navElementContainer.getVirtualRoot();

      // set edge group pairs for selection
      const displayOptionEdges: INavGraphDisplayOptionEdges[] =
         _.cloneDeep(this._store.getState().navigationState.element2stDisplayOptionEdges);
      this.navElementContainer.fillDisplayOptionEdges(displayOptionEdges);
      this._store.dispatch(navigationStore2stDisplayOptionEdges(displayOptionEdges));

      // ------------------------------------------------------------------------------------------
      // -- edge definition config
      // ------------------------------------------------------------------------------------------
      this._edgeDefinitionConfig = this._store.getState().navigationState.element2stEdgeDefinitionConfig;
      if (!this._edgeDefinitionConfig) {
         console.warn('EdgeDefinitionConfig nicht verfügbar');
         return;
      }

      // dump
      if (navigationConfig.debugMode) {
         console.log(this._edgeDefinitionConfig);
      }

      // sanity edgeDirecetion
      if (!this._edgeDefinitionConfig.treeNodeDataEdgeGroup.edgeDirection ||     // FWD oder BKWD
         !this._edgeDefinitionConfig.treeNodeDataEdgeGroup.edgeGroup) {         // z.b. Meldet
         console.warn('EdgeDirection oder EdgeGroup nicht verfügbar');
         return;
      }

      // Edge Grouppe und Richtung der Edge
      const edgeGroup = this._edgeDefinitionConfig.treeNodeDataEdgeGroup.edgeGroup;
      const edgeDir = (this._edgeDefinitionConfig.treeNodeDataEdgeGroup.edgeDirection === 'FWD') ? 'to' : 'from';

      // edgeDefinition reduziert abhängig edgeGroup
      if (!this._edgeDefinitionConfig.edgeDefinitionConfig[edgeGroup]) {
         console.warn('EdgeDefinition für EdgeGroup nicht verfügbar');
         return;
      }
      const edgeDefAndEdgeGroup = this._edgeDefinitionConfig.edgeDefinitionConfig[edgeGroup];

      // edgeDefinition reduziert abhängig von Richtung
      if (!edgeDefAndEdgeGroup[edgeDir]) {
         console.warn('EdgeDefinition für gegebene Kantenrichtung nicht verfügbar');
         return;
      }
      const arrCompTypeToSelect: string[] = edgeDefAndEdgeGroup[edgeDir];

      // sanity
      if (!arrCompTypeToSelect || arrCompTypeToSelect.length === 0) {
         console.warn('KomponentenTypen für EdgeDefinition nicht verfügbar');
         return;
      }
      this._arrCompTypeToSelect = arrCompTypeToSelect;

      // dump
      if (navigationConfig.debugMode) {
         console.log(arrCompTypeToSelect);
      }

      // -- fill result object (part 1)
      this._result.node1Id = this._edgeDefinitionConfig.rootNode.id;
      this._result.node1Group = this._edgeDefinitionConfig.rootNode.group;
      this._result.edgeGroup = this._edgeDefinitionConfig.treeNodeDataEdgeGroup.edgeGroup;
      this._result.edgeDir = this._edgeDefinitionConfig.treeNodeDataEdgeGroup.edgeDirection;
      this._result.nodeTypesToSelect = arrCompTypeToSelect;

      const details = this._store.getState().navigationState.element2stDetail;
      if (details) {
         const node = this._graph.getPositions(details.data._id);
         if (!Object.keys(node).length) {
            this._store.dispatch(navigationStoreResult2stDetail(undefined));
         } else {
            this._graph.selectNodes([details.data._id]);
            this.onSelectNode({
               nodes: [details.data._id],
            });
         }
      }

      NavVisOptions.setInvalidNodes(this._visOptions, this._arrCompTypeToSelect);
      this._graph.setOptions(this._visOptions);
   }

   // ---------------------------------------------------------------------------------------------
   // -- Reset
   // ---------------------------------------------------------------------------------------------
   public reset() {
      this.ngOnDestroy();
      this.navElementContainer = new NavElementsContainer(this._messageSnackService);
      this._store.dispatch(navigationStoreResult2stInitialValue());
      this.ngOnInit();
   }

   // ---------------------------------------------------------------------------------------------
   // -- Event handling
   // ---------------------------------------------------------------------------------------------

   /**
   * Callback function, wird von vis für das handling von Node-Select Events aufgerufen.
   *
   * @param [param: any] Parameter von vis für Interactinen.
   * @returns void
   */
   private onSelectNode(params, update: boolean = false): void {
      for (const node of this._selectedNodes || []) {
         if ((node as string).startsWith('KomponenteGA')) {
            this.navElementContainer.setSVG(node as string, '', '');
         }
      }
      this._selectedNodes = params.nodes;
      for (const node of this._selectedNodes || []) {
         if ((node as string).startsWith('KomponenteGA')) {
            this.navElementContainer.setSVG(node as string,
               '',
               this._visOptions.nodes.color.highlight.border);
         }
      }

      // define neu virtual root
      this.navElementContainer.resetVirtualRoot(params.nodes[0]);
      if (update) {
         // parse searchResult
         this.navElementContainer.applyDisplayFilter(this.elementOptions);
         // setup graph
         this._graph.setData(this.navElementContainer.generateVisData(this._store.getState().navigationState, this.elementOptions));
         this._graph.redraw();
      }
      // set breadcrumb
      const ner: NavElementsRoot = this.navElementContainer.getVirtualRoot();

      // show details
      this.onContextDetails();

      // ------------------------------------------------------------------------------------------
      // Selektion von Elementen zulassen/nichtZulassen
      // ------------------------------------------------------------------------------------------
      // sanity
      if (!this._arrCompTypeToSelect || this._arrCompTypeToSelect.length === 0) {
         return;
      }

      // id -> componente type
      const id: string = params.nodes[0];
      const compType = id.split('/')[0];

      // aktivate/deactivate Selection
      let bCanSelect = false;
      this._arrCompTypeToSelect.forEach((item: string) => {
         if (item === compType) {
            bCanSelect = true;
         }
      });

      // zulässige Komponente selektiert
      if (!bCanSelect) {
         this.canSelectKomponent.emit(null);
         return;
      }

      // -- fill result object (part 2)
      this._result.node2Id = id;
      this._result.node2Group = compType;

      // event to inform container
      this.canSelectKomponent.emit(this._result);
      this._graph.selectNodes(this._selectedNodes, true);
   }

   private onDeselectNode(params) {
      if (!params.nodes.length) {
         const previousSelection = params.previousSelection;
         this._graph.selectNodes(previousSelection.nodes);
      }
   }

   private onDragging(params) {
      if (!this._selectedNodes) {
         this._graph.unselectAll();
      }
      if (this._selectedNodes) {
         try {
            this._graph.selectNodes(this._selectedNodes, true);
         } catch (err) {
            this._selectedNodes = undefined;
         }
      }
   }

   private onDragEnd(params) {
      this._graph.once('dragging', this.onDragging.bind(this));
   }

   private onHoverNode(params) {
      this._graph.setOptions({ interaction: { hoverConnectedEdges: true } });
   }

   private onBlurEdge(params) {
      this._graph.setOptions({ interaction: { hoverConnectedEdges: false } });
   }

   private unselectNode() {
      this._selectedNodes = null;
      this._graph.unselectAll();
      this.canSelectKomponent.emit(null);
   }

   /**
   * Callback function, wird von vis für das handling Right-Mouse Event aufgerufen.
   *
   * @param [param: any] Parameter object von vis mit diserven Daten. Mehr Infos in der vis Dokumentation.
   * @returns void
   */
   private onContext(params): void {
      params.event.preventDefault();

      // find node beyound mouse
      this._rightClickSelectedNodeID = '';
      const nodeID = this._graph.getNodeAt(
         {
            x: params.event.offsetX,
            y: params.event.offsetY
         }
      );

      if (!nodeID) {
         return;
      }

      if (nodeID.toString() === navigationConfig.defaultValuesForRoot.id) {
         return;
      }

      // remember selected ID
      this._rightClickSelectedNodeID = nodeID;

      const x = params.event.pageX;
      const y = params.event.pageY;

      this._contextMenuContainerElemRef.nativeElement.style.left = x - 142 + 'px';
      this._contextMenuContainerElemRef.nativeElement.style.top = y - 121 + 'px';

      this._contextMenuTrigger.openMenu();
   }

   /**
   * Callback function, wird von vis für das zoom Event aufgerufen.
   *
   * @param [param: any] Parameter object von vis mit diserven Daten. Mehr Infos in der vis Dokumentation.
   * @returns void
   */
   private onZoom(graph: vis.Network, maxscale: number, params): void {
      if (params.direction === '-' && params.scale <= maxscale) {
         const moveToOptions: vis.MoveToOptions = {
            position: { x: 0, y: 0 },
            scale: maxscale
         };
         graph.moveTo(moveToOptions);
      }
   }

   private onStabilizationProgress(params) {
      if (!this._loadingPhase) {
         this._loadingPhase = true;
      }
   }

   private onStabilizationIterationsDone(params) {
      this._loadingPhase = false;
   }

   /**
   * Handler function zum Popup Menü.
   * Initiiert Request zum laden von Details.
   *
   * @param keine
   * @returns [void]
   */
   public onContextDetails(): void {
      // sanity
      if (!this.navElementContainer.getVirtualRoot()) {
         console.warn('Kein virtueller root');
         return;
      }

      // Suchergebnis
      if (this.navElementContainer.getVirtualRootId() === navigationConfig.defaultValuesForRoot.id) {
         return;
      }

      this._loadingPhase = true;

      // -->>ASY
      this._navigationService.loadElements2stDetail(
         this.navElementContainer.getVirtualRootId()
      )
         .then((boolResultASY: IBoolResult) => {
            this._loadingPhase = false;

            // result available
            if (boolResultASY.result) {
               return;
            }

            // no data
            this._messageSnackService.showSnackWarning(boolResultASY.message.message, 'NavSearchGraphViewComponent');
         })
         .catch((err: ErrorMessage) => {
            this._loadingPhase = false;
            this._messageSnackService.showSnackError(err.message, 'NavSearchGraphViewComponent');
         });
   }

   /**
   * Handler function zum Popup Menü.
   * Initiiert Request zum nachladen von Elementen.
   *
   * @param keine
   * @returns [void]
   */
   public onContextExpandNode(): void {
      // Suchergebnis
      if (this._rightClickSelectedNodeID === navigationConfig.defaultValuesForRoot.id) {
         this._messageSnackService.showSnackWarning('Suchergebnis kann nicht expandiert werden', 'NavSearchGraphViewComponent');
         return;
      }

      this._loadingPhase = true;

      // set object rootToExtend in navElementContainer - to verify 'root' of result from backend
      this.navElementContainer.setRootToExtend(this._rightClickSelectedNodeID);

      // -->> ASYNC
      this._navigationService.loadElements2stExtension(
         this._rightClickSelectedNodeID,
      )
         .then((boolResultASY: IBoolResult) => {
            this._loadingPhase = false;

            // result available
            if (boolResultASY.result) {
               // parse searchResult
               this.navElementContainer.computeExtendedSearchResult(
                  this._store.getState().navigationState.elementsResult2stExtended,
                  this._store.getState().navigationState.config);
               this.navElementContainer.applyDisplayFilter(this.elementOptions);
               // update graph
               this._graph.setData(this.navElementContainer.generateVisData(this._store.getState().navigationState, this.elementOptions));
               this._graph.redraw();

               // set edge group pairs for selection
               const displayOptionEdges: INavGraphDisplayOptionEdges[] =
                  _.cloneDeep(this._store.getState().navigationState.element2stDisplayOptionEdges);
               this.navElementContainer.fillDisplayOptionEdges(displayOptionEdges);
               this._store.dispatch(navigationStore2stDisplayOptionEdges(displayOptionEdges));

               // clean up
               return;
            }

            // no data
            this._messageSnackService.showSnackWarning(boolResultASY.message.message, 'NavSearchGraphViewComponent');
         })
         .catch((err: ErrorMessage) => {
            this._loadingPhase = false;
            this._messageSnackService.showSnackError(err.message, 'NavSearchGraphViewComponent');
         });
   }

   /**
   * Handler function zum Popup Menü.
   * Initiiert Request initialen Request für das selektierte Elementen.
   *
   * @param keine
   * @returns [void]
   */
   public onContextReduceNode(): void {
      // Suchergebnis
      if (this._rightClickSelectedNodeID === navigationConfig.defaultValuesForRoot.id) {
         this._messageSnackService.showSnackWarning('Suchergebnis kann nicht reduziert werden', 'NavSearchGraphViewComponent');
         return;
      }

      this._loadingPhase = true;

      // -->> ASYNC
      this._navigationService.loadElements2stReduced(
         this._rightClickSelectedNodeID
      )
         .then((boolResultASY: IBoolResult) => {
            this._loadingPhase = false;

            // result available
            if (boolResultASY.result) {
               return;
            }

            // no data
            this._messageSnackService.showSnackWarning(boolResultASY.message.message, 'NavSearchGraphViewComponent');
         })
         .catch((err: ErrorMessage) => {
            this._loadingPhase = false;
            this._messageSnackService.showSnackError(err.message, 'NavSearchGraphViewComponent');
         });
   }

}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/nav/compv/nav-graph2/nav-graph2.ts